import { useEffect, useState, useRef } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch } from '../../hooks';
import { fetchMenu } from '../menu/menuApi';

import { useUpdateDynamicMenuSequenceMutation } from './dynamicMenuApi';

import type { IAllMenu, OrderData } from './model';

interface UpdateMenuResponse {
  settings: {
    message: string;
  };
}

export const useMenuDragDrop = (initialMenu: IAllMenu[]) => {
  const dispatch = useAppDispatch();
  const [allMenu, setAllMenu] = useState<IAllMenu[]>(initialMenu);
  const [updateDynamicMenuSequence, { isLoading: isUpdating }] =
    useUpdateDynamicMenuSequenceMutation();

  const pendingUpdateRef = useRef<{
    type: 'main' | 'submenu';
    parentId?: number;
    menuData: IAllMenu[];
  } | null>(null);

  useEffect(() => {
    if (initialMenu.length > 0) {
      setAllMenu(initialMenu);
    }
  }, [initialMenu]);

  const buildSubmenuPayload = (
    menuData: IAllMenu[],
    parentId: number
  ): { sequences: OrderData[] } => {
    const parentMenu = menuData.find((menu) => menu.adminMenuId === parentId);
    const submenuSequences: OrderData[] =
      parentMenu?.subMenu?.map((subItem, index) => ({
        menuId: subItem.adminMenuId,
        order: index + 1,
      })) ?? [];
    return { sequences: submenuSequences };
  };

  const buildMainMenuPayload = (
    menuData: IAllMenu[]
  ): { sequences: OrderData[] } => {
    const allSequences: OrderData[] = [];
    menuData.forEach((item, index) => {
      allSequences.push({ menuId: item.adminMenuId, order: index + 1 });
      item.subMenu?.forEach((subItem, subIndex) => {
        allSequences.push({ menuId: subItem.adminMenuId, order: subIndex + 1 });
      });
    });
    return { sequences: allSequences };
  };

  const processPendingUpdate = async (): Promise<void> => {
    if (!pendingUpdateRef.current) return;

    const { type, parentId, menuData } = pendingUpdateRef.current;
    const payload =
      type === 'submenu' && parentId !== undefined
        ? buildSubmenuPayload(menuData, parentId)
        : buildMainMenuPayload(menuData);

    const promise = updateDynamicMenuSequence(
      payload
    ).unwrap() as Promise<UpdateMenuResponse>;

    await toast.promise<UpdateMenuResponse>(promise, {
      loading: 'Saving order…',
      success: (res) => res.settings.message,
      error: (err: unknown) =>
        err?.data?.settings?.message ?? 'Failed to update menu order',
    });

    try {
      await promise;
      dispatch(fetchMenu());
    } catch (err) {
      console.error('Failed to update menu order:', err);
    } finally {
      pendingUpdateRef.current = null;
    }
  };

  useEffect(() => {
    if (!pendingUpdateRef.current) return;
    const timeoutId = setTimeout(processPendingUpdate, 300);
    return () => clearTimeout(timeoutId);
  }, [allMenu]);

  const moveItem = <T>(array: T[], from: number, to: number): T[] => {
    const newArray = [...array];
    const [removed] = newArray.splice(from, 1);
    newArray.splice(to, 0, removed);
    return newArray;
  };

  const handleDrop = (dragIndex: number, hoverIndex: number): void => {
    setAllMenu((prev) => {
      const newMenu = moveItem(prev, dragIndex, hoverIndex);
      pendingUpdateRef.current = { type: 'main', menuData: newMenu };
      return newMenu;
    });
  };

  const handleSubMenuDrop = (
    parentId: number,
    dragIndex: number,
    hoverIndex: number
  ): void => {
    setAllMenu((prevMenu) => {
      const newMenu = [...prevMenu];
      const parentMenuIndex = newMenu.findIndex(
        (m) => m.adminMenuId === parentId
      );

      if (parentMenuIndex !== -1 && newMenu[parentMenuIndex].subMenu) {
        const newSubMenu = moveItem(
          newMenu[parentMenuIndex].subMenu,
          dragIndex,
          hoverIndex
        );
        newMenu[parentMenuIndex] = {
          ...newMenu[parentMenuIndex],
          subMenu: newSubMenu,
        };
        pendingUpdateRef.current = {
          type: 'submenu',
          parentId,
          menuData: newMenu,
        };
      }
      return newMenu;
    });
  };

  return {
    allMenu,
    handleDrop,
    handleSubMenuDrop,
    isUpdating,
  };
};
